import {
  AppointmentDurationInNumbers,
  AppointmentSpeciality,
} from '@avanta/shared/constants/appointments';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { SPECIALITIES_WITH_SHARED_COUPONS } from 'constants/appointments';
import { QUERY_KEYS } from 'constants/common';
import { CouponsService } from 'services/coupons';
import { useUserStore } from 'stores/user';
import { type Coupon } from 'types/coupons';
import { getUserId } from 'utils/storage';
import { useFetchUser } from './useFetchUser';

const DURATION_DOUBLE = AppointmentDurationInNumbers.DURATION_DOUBLE;

function getAvailableCoupons(
  specialityId: string | undefined,
  couponsCount: Record<string, number>,
  couponSessions: number
): number {
  if (!specialityId) return couponSessions;

  if (SPECIALITIES_WITH_SHARED_COUPONS.has(Number(specialityId))) {
    return (
      (couponsCount[AppointmentSpeciality.ADULT_PSYCHOLOGY] ?? 0) +
      (couponsCount[AppointmentSpeciality.PAE] ?? 0)
    );
  }

  return couponsCount[specialityId] ?? 0;
}

function countBySpecialities(coupons: Coupon[]): Record<number, number> {
  return coupons.reduce(
    (acc, coupon) => {
      const speciality = coupon.specialitiesBookable;
      acc[speciality] = (acc[speciality] || 0) + 1;
      return acc;
    },
    {} as Record<number, number>
  );
}

function calculateSessions(count: number): number {
  return Math.floor(count / DURATION_DOUBLE);
}

const useFetchCoupons = (userId: string, enabled?: boolean): UseQueryResult<Coupon[]> =>
  useQuery<AxiosResponse<Coupon[]>, Error, Coupon[]>({
    queryKey: [QUERY_KEYS.FETCH_COUPONS, userId],
    queryFn: () => CouponsService.getCoupons(userId),
    select: response => response.data,
    enabled: !!userId && enabled,
  });

export const useCoupons = (
  userId?: string,
  specialityId?: number,
  enabled = true
): {
  coupons: Coupon[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  couponsCount: Record<number, number>;
  remainingSessionsThisMonth: number;
  remainingSessions: number;
  hasCopaid: boolean;
  enterpriseSessions: number;
} => {
  const userId_ = userId ?? getUserId();
  const patientQuery = useFetchUser(userId);
  const patientStore = useUserStore(state => state.user);
  const { data: coupons, isLoading, isFetching } = useFetchCoupons(userId_, enabled);
  const patient = patientQuery?.data ?? patientStore;
  const grantedSessionsThisMonth = patient?.grantedSessionsThisMonth ?? 0;
  const grantedSessionsThisYear = patient?.grantedSessionsThisYear ?? 0;
  const rawRemaining = patient?.remainingSessionsThisMonth ?? 0;
  const hasCopaid = patient?.hasCopaid ?? false;
  const couponsCount = useMemo(() => {
    if (!coupons?.length) return {};
    const rawCount = countBySpecialities(coupons);
    return Object.entries(rawCount).reduce(
      (acc, [key, value]) => {
        acc[Number(key)] = calculateSessions(value);
        return acc;
      },
      {} as Record<number, number>
    );
  }, [coupons]);

  const calculations = useMemo(() => {
    const hasLimit = grantedSessionsThisMonth > -1 || grantedSessionsThisYear > -1;
    const couponSessions = coupons?.length ? calculateSessions(coupons.length) : 0;

    const specialityCoupons = getAvailableCoupons(
      specialityId?.toString(),
      couponsCount,
      couponSessions
    );

    let remainingSessionsThisMonth = calculateSessions(rawRemaining);
    if (!hasLimit && rawRemaining === -1) {
      remainingSessionsThisMonth = Infinity;
    }

    const enterpriseSessions = !hasLimit ? calculateSessions(rawRemaining) : 0;
    const remainingSessions =
      !hasCopaid && hasLimit ? remainingSessionsThisMonth + specialityCoupons : specialityCoupons;

    return {
      remainingSessionsThisMonth,
      remainingSessions,
      enterpriseSessions,
    };
  }, [
    grantedSessionsThisMonth,
    grantedSessionsThisYear,
    rawRemaining,
    coupons,
    couponsCount,
    specialityId,
    hasCopaid,
  ]);

  return {
    coupons,
    isLoading,
    isFetching,
    couponsCount,
    hasCopaid,
    ...calculations,
  };
};
